import {
  CSSProperties,
  forwardRef,
  Fragment,
  ReactNode,
  Ref,
  useRef,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { IconType } from '@react-icons/all-files/lib';
import { CgClose } from '@react-icons/all-files/cg/CgClose';

const SIZES = {
  xsmall: 'sm:max-w-xs',
  small: 'sm:max-w-sm',
  medium: 'sm:max-w-md',
  large: 'sm:max-w-xl',
  xlarge: 'sm:max-w-3xl',
  xxlarge: 'sm:max-w-4xl',
  xxxlarge: 'sm:max-w-7xl',
  max: 'sm:max-w-[80vw]',
};

export type ModalSize = keyof typeof SIZES;

const OVERLAY_CLASS =
  'fixed inset-0 bg-gray-400 bg-opacity-75 transition-opacity';

export interface ModalProps {
  isOpen: boolean;
  title?: ReactNode;
  children?: string | ReactNode;
  size?: keyof typeof SIZES;
  overlayDismiss?: boolean;
  onDismiss: () => void;
  icon?: IconType;
  iconStyle?: string;
  hideOverflow?: boolean;
  className?: string;
  bodyStyle?: CSSProperties;
  showCloseButton?: boolean;
  style?: CSSProperties;
  bodyClassName?: string;
  showHeader?: boolean;
}

export const ModalCloseButton = forwardRef(
  (
    props: {
      onClick: () => void;
      className?: string;
    },
    ref?: Ref<HTMLButtonElement>,
  ) => (
    <button
      type="button"
      tabIndex={-1}
      onClick={props.onClick}
      className={classNames(props.className || 'ml-2')}
      data-test-id="close-modal-button"
      ref={ref}
    >
      <CgClose />
    </button>
  ),
);

export const Modal = forwardRef(
  (
    {
      onDismiss,
      overlayDismiss = true,
      hideOverflow = true,
      size = 'small',
      children,
      isOpen,
      title,
      icon,
      iconStyle,
      className,
      showCloseButton = true,
      style,
      bodyStyle,
      bodyClassName,
      showHeader = true,
    }: ModalProps,
    ref,
  ) => {
    const Icon = icon;
    const closeButtonRef = useRef(null);
    return (
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-40 overflow-y-hidden sm:overflow-y-auto"
          open={isOpen}
          onClose={onDismiss}
          initialFocus={closeButtonRef}
        >
          <div
            className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
            data-test-id="modal"
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {overlayDismiss ? (
                <Dialog.Overlay className={OVERLAY_CLASS} />
              ) : (
                <div className={OVERLAY_CLASS} />
              )}
            </Transition.Child>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  'inline-block bg-white w-screen rounded-lg text-left shadow-xl transform transition-all my-auto sm:my-8 align-middle sm:w-full',
                  SIZES[size],
                  hideOverflow ? 'overflow-hidden' : '',
                  className,
                  !showHeader && 'relative',
                )}
                style={style}
              >
                {showHeader ? (
                  <>
                    <Dialog.Title
                      as="div"
                      className="flex justify-between px-6 py-5 text-lg text-gray-700"
                      ref={ref as any}
                    >
                      <div className="flex items-center justify-start">
                        {!!Icon && (
                          <div className="flex items-center justify-between mr-3">
                            <span
                              className={classNames(
                                'rounded-lg p-3',
                                iconStyle,
                              )}
                            >
                              <Icon className="text-xl" />
                            </span>
                          </div>
                        )}
                        {title}
                      </div>
                      {showCloseButton && (
                        <ModalCloseButton
                          onClick={onDismiss}
                          ref={closeButtonRef}
                        />
                      )}
                    </Dialog.Title>
                    {title && <hr className="border-gray-300" />}
                  </>
                ) : (
                  <>
                    {showCloseButton && (
                      <ModalCloseButton
                        onClick={onDismiss}
                        ref={closeButtonRef}
                        className="absolute top-7 right-7"
                      />
                    )}
                  </>
                )}
                <div
                  className={classNames(
                    bodyClassName || 'px-6 py-5 text-sm text-gray-500',
                  )}
                  style={bodyStyle}
                >
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    );
  },
);
